// RITA-1498 (Nutzungserfassung Phase 1b) — zentrale Aufnahmestelle.
//
// Erzeuger (Apollo-Link in components/ErrorMessage, axios-Interceptor, Fehlergrenze,
// Klick-Listener, Router-Hook) melden hier; nur der UsageTrackingProvider hängt eine Senke
// (den Puffer) an. Ohne Senke — Erfassung aus, nicht angemeldet, Provider nicht montiert —
// wird still verworfen. Der Tracker ergänzt Route und Zeitstempel. Nichts hier darf werfen.
import type { IUsageEvent, UsageEventType, UsagePropValue } from 'global-intern-types';
import { UNKNOWN_ROUTE } from './routePattern';

export type ErrorSource = 'boundary' | 'window' | 'promise' | 'graphql';

export interface UsageEventInput {
  type: UsageEventType;
  ts?: number;
  route?: string;
  name?: string;
  value?: number;
  props?: Record<string, UsagePropValue>;
}

export type UsageSink = (event: IUsageEvent) => void;

/**
 * Meldeweg eines Erzeugers. Bausteine bekommen ihn als Parameter (nicht den ganzen Tracker),
 * damit Tests eine eigene Aufnahmestelle einsetzen können; Komponenten reichen
 * `usageTracker.record` durch.
 */
export type UsageRecord = (input: UsageEventInput) => boolean;

export interface UsageTracker {
  attachSink(sink: UsageSink): void;
  detachSink(): void;
  isActive(): boolean;
  setRoute(route: string): void;
  getRoute(): string;
  /**
   * Welche Route galt zum Zeitpunkt `ts`? Web Vitals werden spaet gemeldet (RITA-1515), der
   * Messwert gehoert aber zu der Seite, die waehrend der Messung offen war. Vor dem ersten
   * bekannten Eintrag gilt die fruehste bekannte Route, ohne Verlauf die aktuelle.
   */
  routeAt(ts: number): string;
  /** true = an die Senke übergeben; false = verworfen (keine Senke oder Senke kaputt). */
  record(input: UsageEventInput): boolean;
  reportError(source: ErrorSource, error: unknown): boolean;
}

/** Laenge des Routenverlaufs (RITA-1515): reicht fuer ein Tab-Leben, deckelt den Speicher. */
export const MAX_ROUTE_HISTORY = 200;
export const MAX_ERROR_MESSAGE_LENGTH = 200;
export const MAX_STACK_TOP_LENGTH = 300;
export const STACK_TOP_LINES = 3;
export const UNKNOWN_ERROR_MESSAGE = 'unbekannter Fehler';

export interface ErrorDescription {
  message: string;
  stackTop?: string;
}

/** Meldung (gekürzt) und oberste Stack-Zeilen — nie das ganze Fehlerobjekt (Konzept 3.5). */
export const describeError = (error: unknown): ErrorDescription => {
  if (typeof error === 'string') {
    return { message: error.trim() ? error.slice(0, MAX_ERROR_MESSAGE_LENGTH) : UNKNOWN_ERROR_MESSAGE };
  }
  if (typeof error === 'object' && error !== null) {
    const { message, stack } = error as { message?: unknown; stack?: unknown };
    const text = typeof message === 'string' && message.trim() ? message : UNKNOWN_ERROR_MESSAGE;
    const description: ErrorDescription = { message: text.slice(0, MAX_ERROR_MESSAGE_LENGTH) };
    if (typeof stack === 'string' && stack.trim()) {
      description.stackTop = stack.split('\n').slice(0, STACK_TOP_LINES).join('\n').slice(0, MAX_STACK_TOP_LENGTH);
    }
    return description;
  }
  return { message: UNKNOWN_ERROR_MESSAGE };
};

export const createTracker = ({ now = () => Date.now() }: { now?: () => number } = {}): UsageTracker => {
  let sink: UsageSink | null = null;
  let route: string = UNKNOWN_ROUTE;
  // Ab wann galt welche Route? Aufsteigend nach ts, aelteste Eintraege fallen weg.
  const history: { ts: number; route: string }[] = [];

  const record = (input: UsageEventInput): boolean => {
    if (!sink) return false;
    const event: IUsageEvent = {
      type: input.type,
      ts: input.ts ?? now(),
      route: input.route ?? route,
    };
    if (input.name !== undefined) event.name = input.name;
    if (input.value !== undefined) event.value = input.value;
    if (input.props !== undefined) event.props = input.props;
    try {
      sink(event);
      return true;
    } catch {
      return false;
    }
  };

  return {
    attachSink(next: UsageSink): void {
      sink = next;
    },
    detachSink(): void {
      sink = null;
    },
    isActive: () => sink !== null,
    setRoute(next: string): void {
      route = next || UNKNOWN_ROUTE;
      history.push({ ts: now(), route });
      if (history.length > MAX_ROUTE_HISTORY) history.splice(0, history.length - MAX_ROUTE_HISTORY);
    },
    getRoute: () => route,
    routeAt(ts: number): string {
      if (history.length === 0) return route;
      for (let index = history.length - 1; index >= 0; index -= 1) {
        if (history[index].ts <= ts) return history[index].route;
      }
      return history[0].route;
    },
    record,
    reportError(source: ErrorSource, error: unknown): boolean {
      const { message, stackTop } = describeError(error);
      const props: Record<string, UsagePropValue> = { source };
      if (stackTop) props.stack_top = stackTop;
      return record({ type: 'error', name: message, props });
    },
  };
};

/** Die eine Aufnahmestelle der App. Tests bauen sich eigene Instanzen über createTracker. */
export const usageTracker: UsageTracker = createTracker();
